/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import ChannelStore from '@app/stores/ChannelStore';
import DeveloperOptionsStore from '@app/stores/DeveloperOptionsStore';
import GuildNSFWAgreeStore, {NSFWGateReason} from '@app/stores/GuildNSFWAgreeStore';
import GuildStore from '@app/stores/GuildStore';
import RelationshipStore from '@app/stores/RelationshipStore';
import UserSettingsStore from '@app/stores/UserSettingsStore';
import {ChannelTypes} from '@fluxer/constants/src/ChannelConstants';
import {GuildNSFWLevel} from '@fluxer/constants/src/GuildConstants';
import {RelationshipTypes, SensitiveMediaFilterLevel} from '@fluxer/constants/src/UserConstants';
import {useCallback, useEffect, useState} from 'react';

interface NSFWMediaResult {
	shouldBlur: boolean;
	shouldBlock: boolean;
	gateReason: NSFWGateReason;
	canReveal: boolean;
	reveal: () => void;
}

function getSensitiveFilterLevel(channelId: string | undefined): number {
	if (!channelId) {
		return SensitiveMediaFilterLevel.SHOW;
	}

	const channel = ChannelStore.getChannel(channelId);
	if (!channel) {
		return SensitiveMediaFilterLevel.SHOW;
	}

	if (channel.type === ChannelTypes.DM || channel.type === ChannelTypes.GROUP_DM) {
		if (channel.type === ChannelTypes.DM && channel.recipientIds.length > 0) {
			const recipientId = channel.recipientIds[0];
			const relationship = RelationshipStore.getRelationship(recipientId);
			if (relationship?.type === RelationshipTypes.FRIEND) {
				return UserSettingsStore.sensitiveContentFriendDmFilter;
			}
		}
		return UserSettingsStore.sensitiveContentNonFriendDmFilter;
	}

	if (channel.guildId) {
		if (channel.nsfw) {
			return SensitiveMediaFilterLevel.SHOW;
		}

		const guild = GuildStore.getGuild(channel.guildId);
		if (guild && (guild.nsfwLevel === GuildNSFWLevel.EXPLICIT || guild.nsfwLevel === GuildNSFWLevel.AGE_RESTRICTED)) {
			return SensitiveMediaFilterLevel.SHOW;
		}

		return UserSettingsStore.sensitiveContentGuildFilter;
	}

	return SensitiveMediaFilterLevel.SHOW;
}

export function useNSFWMedia(nsfw: boolean | undefined, channelId: string | undefined): NSFWMediaResult {
	const mockNSFWMediaGateReason = DeveloperOptionsStore.mockNSFWMediaGateReason;
	const forceMediaNSFW = DeveloperOptionsStore.forceMediaNSFW;
	const [isRevealed, setIsRevealed] = useState(false);

	const hasGateableContext = channelId ? GuildNSFWAgreeStore.isGatedContent({channelId}) : false;
	const effectiveNSFW = forceMediaNSFW || mockNSFWMediaGateReason !== 'none' || !!nsfw || hasGateableContext;
	const gateReasonFromStore = GuildNSFWAgreeStore.getGateReason({channelId: channelId ?? null});

	let gateReason: NSFWGateReason;
	if (mockNSFWMediaGateReason !== 'none') {
		gateReason =
			mockNSFWMediaGateReason === 'geo_restricted' ? NSFWGateReason.GEO_RESTRICTED : NSFWGateReason.AGE_RESTRICTED;
	} else if (effectiveNSFW && channelId) {
		gateReason = gateReasonFromStore;
	} else {
		gateReason = NSFWGateReason.NONE;
	}

	const filterLevel = getSensitiveFilterLevel(channelId);
	const isFilterBlur = effectiveNSFW && filterLevel === SensitiveMediaFilterLevel.BLUR;
	const isFilterBlock = effectiveNSFW && filterLevel === SensitiveMediaFilterLevel.BLOCK;
	const isGateBlur =
		effectiveNSFW && gateReason !== NSFWGateReason.NONE && gateReason !== NSFWGateReason.CONSENT_REQUIRED;

	const canReveal = isFilterBlur && !isGateBlur;
	const shouldBlur = (isGateBlur || isFilterBlur) && !(canReveal && isRevealed);

	const shouldBlock: boolean = isFilterBlock;

	useEffect(() => {
		setIsRevealed(false);
	}, [channelId, gateReason, mockNSFWMediaGateReason, forceMediaNSFW, nsfw, filterLevel]);

	const reveal = useCallback(() => {
		if (!canReveal) return;
		setIsRevealed(true);
	}, [canReveal]);

	return {shouldBlur, gateReason, shouldBlock, canReveal, reveal};
}
